




































































































































import { Component, Watch } from "vue-property-decorator";
import LanguagePickerComponent from "@/components/common/LanguagePickerComponent.vue";
import { changeLanguageTo, Language } from "@/i18n/Languages";
import { 
  getClientLanguage, 
  getGeoEncodingAccess,
  getLangugageSettingsForReports,
} from "@/data/repository/ClientRepository";
import { UserModule } from "@/store/modules/UserModule";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { defineOwnCompanySettingsClientAccess } from "@/permissions/ClientPermissions";
import {
  AddressTemplateField,
  CompanySettings,
  DEFAULT_ADDRESS_FORMAT_TEMPLATE,
} from "@/data/models/Settings";
import TemplateFieldEditorComponent from "@/components/common/TemplateFieldEditor.vue";
import { ADDRESS_TEMPLATE_SAMPLE, renderAddressTemplate } from "@/utils/AddressTemplate";
import { CompanySettingsModule } from "@/store/modules/CompanySettingsModule";
import { createDefaultCompanySettings  } from "@/data/models/Client";
import { updateCompanySettings } from "@/data/repository/SettingsRepository";
import { DEFAULT_LOCALE } from "@/plugins/vuetify";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { getSupportedTimezones, DEFAULT_TIMEZONE } from "@/utils/TimezoneHelper";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";

@Component({
  components: {
    "language-picker": LanguagePickerComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "template-field-editor": TemplateFieldEditorComponent,
    picker: PickerDialogComponent
  },
})
export default class CompanySettingsComponent extends SettingsPageMixin {
  isInitializing = true;

  yesNoValues = [true, false];

  usePrimaryLanguageForReports = true;
  preferAbsHumidity = true;
  timezone = DEFAULT_TIMEZONE;

  /** Hans's address format. Initialised to today's output, so opening the page changes nothing. */
  addressFormatTemplate: AddressTemplateField[] = [...DEFAULT_ADDRESS_FORMAT_TEMPLATE];

  addressCommaField = AddressTemplateField.COMMA;

  /**
   * Whether this client works with addresses on the map. Off unless asked for (Hans: "Init: NEIN"),
   * so a client that never opens this page is unaffected.
   */
  enableAddressGeocoding = false;

  language = this.defaultLanguage;

  initialCompanySettings: CompanySettings = createDefaultCompanySettings();

  @Watch("companySettings")
  onReportSettingsChanged() {
    if (this.companySettings && !this.companySettings.areDefaultCompanySettings) {
      this.initializeParameters(this.companySettings);  
    }
  }

  @Watch("projectManagementSettings")
  onProjectManagementSettingsChanged() {
    const projectManagerSettings = this.projectManagerSettings;

    if (
      projectManagerSettings && 
      !projectManagerSettings.areDefaultProjectManagerSettings &&
      this.initialCompanySettings.preferAbsHumidity !== projectManagerSettings.preferAbsHumidity &&
      this.companySettings.preferAbsHumidity === undefined
    ) {
      this.companySettings.preferAbsHumidity = projectManagerSettings.preferAbsHumidity;
      this.initializeParameters(this.companySettings);  
    }
  }

  get hasChanged() {
    return (
      this.language !== this.initialCompanySettings.language ||
      this.usePrimaryLanguageForReports !== this.initialCompanySettings.usePrimaryLanguageForReports ||
      this.preferAbsHumidity !== this.initialCompanySettings.preferAbsHumidity ||
      this.timezone !== this.initialCompanySettings.timezone ||
      this.addressFormatTemplate.join(",") !==
        (this.initialCompanySettings.addressFormatTemplate ?? []).join(",") ||
      this.enableAddressGeocoding !==
        (this.initialCompanySettings.enableAddressGeocoding ?? false)
    );
  }

  get addressTemplateFields(): AddressTemplateField[] {
    return Object.values(AddressTemplateField);
  }

  /** Written with sample data, so the preview never depends on a project being open. */
  get addressFormatPreview(): string {
    return renderAddressTemplate(this.addressFormatTemplate, ADDRESS_TEMPLATE_SAMPLE);
  }

  formatAddressTemplateField(field: string): string {
    return this.$t(`settings.companySettings.addressFormat.templateFields.${field}`).toString();
  }

  get timezoneOptions() {
    return getSupportedTimezones();
  }

  get companySettings() {
    return CompanySettingsModule.companySettings;
  }

  get projectManagementSettings() {
    return ProjectManagementSettingsModule.projectManagementSettings;
  }

  get projectManagerSettings() {
    return this.projectManagementSettings.projectManagerSettings;
  }

  get canEdit() {
    return defineOwnCompanySettingsClientAccess();
  }

  get readonly() {
    return !this.isEditMode || !this.canEdit;
  }

  get defaultLanguage() {
    return DEFAULT_LOCALE;
  }

  /** KARTE. Without it there are no map features for `enableAddressGeocoding` to reveal. */
  get isMapEnabled() {
    return getGeoEncodingAccess();
  }

  formatYesNo(value: boolean) {
    return this.$t(`common.${value ? "yes" : "no"}`).toString();
  }

  formatPreferAbsHumidity(value: boolean) {
    return this.$t(`settings.companySettings.${value ? "absoluteHumidity" : "mixingRatio"}`).toString();
  }

  initializeParameters(
    companySettings: CompanySettings
  ) {
    this.language = companySettings.language;
    this.usePrimaryLanguageForReports = companySettings.usePrimaryLanguageForReports;

    if (companySettings.preferAbsHumidity !== undefined) {
      this.preferAbsHumidity = companySettings.preferAbsHumidity;
    }

    this.timezone = companySettings.timezone ?? DEFAULT_TIMEZONE;

    // A client that has never configured this sees the format the products already render, rather
    // than an empty row that reads as "no address will be shown".
    this.addressFormatTemplate = [
      ...(companySettings.addressFormatTemplate ?? DEFAULT_ADDRESS_FORMAT_TEMPLATE),
    ];

    this.enableAddressGeocoding = companySettings.enableAddressGeocoding ?? false;

    this.initialCompanySettings = {
      language: this.language,
      usePrimaryLanguageForReports: this.usePrimaryLanguageForReports,
      preferAbsHumidity: this.preferAbsHumidity,
      timezone: this.timezone,
      addressFormatTemplate: [...this.addressFormatTemplate],
      enableAddressGeocoding: this.enableAddressGeocoding,
    };

    this.isEditMode = false;
  }

  onLanguageChanged(language: Language) {
    this.language = language;
  }

  async created() {
    this.isInitializing = true;
    const companySettings = CompanySettingsModule.companySettings;
    
    const usePrimaryLanguageForReports = getLangugageSettingsForReports();
    const defaultLanguge = getClientLanguage();
    const preferAbsHumidity = companySettings.preferAbsHumidity !== undefined
      ? companySettings.preferAbsHumidity
      : this.projectManagerSettings?.preferAbsHumidity ?? true;

    if (companySettings && !companySettings.areDefaultCompanySettings) {
      companySettings.preferAbsHumidity = preferAbsHumidity;

      this.initializeParameters(companySettings);
    }
    else {
      this.initializeParameters({
        language: defaultLanguge,
        usePrimaryLanguageForReports: usePrimaryLanguageForReports,
        preferAbsHumidity: preferAbsHumidity,
      });
    }

    this.isInitializing = false;
  }

  async doSave() {
    const update: CompanySettings = {
      language: this.language,
      usePrimaryLanguageForReports: this.usePrimaryLanguageForReports,
      preferAbsHumidity: this.preferAbsHumidity,
      timezone: this.timezone,
      addressFormatTemplate: this.addressFormatTemplate,
      // Persisted even when KARTE is off, so switching KARTE back on restores what the client chose
      // rather than silently resetting it to NEIN.
      enableAddressGeocoding: this.enableAddressGeocoding,
    }

    await updateCompanySettings(update);

    const user = UserModule.activeUser;
    if (user && !user.language) {
      changeLanguageTo(this.language);
    }
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.companySettings.companySettingsTitle").toString());
  }
}
